// ── Collision System ──
// Capsule-vs-BVH collision for proper slope, ramp, and stair support.
//
// The player is modelled as a capsule (line segment + radius) and collision
// is resolved against the world's triangle mesh via three-mesh-bvh's shapecast.
//
// The shapecast runs in an iterative loop (up to MAX_ITERATIONS passes).
// Each pass:
//   1. Builds a fresh AABB from the current capsule position
//   2. Runs a single shapecast — pushes the capsule for EVERY penetrating
//      triangle found (modifying the capsule mid-pass is fine because the
//      AABB is NOT updated during the pass, so the BVH traversal sees a
//      stable broad-phase region and visits all relevant subtrees)
//   3. After the pass, if the capsule moved, loops again with a fresh AABB
//      to catch any new geometry the capsule has moved into
//
// This matches the approach from the three.js games_fps.html example and
// the gkjohnson/three-mesh-bvh physics demo.
//
// References:
//   - three-mesh-bvh physics example (gkjohnson/three-mesh-bvh)
//   - three.js games_fps.html (Octree + Capsule approach)
//   - Source engine sv_standable_normal = 0.7

import * as THREE from 'three';
import type { MeshBVH } from 'three-mesh-bvh';
import { ARENA_HALF } from '../renderer/scene';

// ── BVH reference (set by scene.ts after building the collision mesh) ──

let worldBVH: MeshBVH | null = null;
let worldBVHMesh: THREE.Mesh | null = null;

/** Set the collision BVH built from scene geometry. Called by scene.ts. */
export function setCollisionBVH(bvh: MeshBVH, mesh: THREE.Mesh): void {
  worldBVH = bvh;
  worldBVHMesh = mesh;
}

/** Check if the BVH collision system is ready. */
export function hasCollisionBVH(): boolean {
  return worldBVH !== null;
}

/**
 * Read-only handle to the active world BVH for ancillary lookups
 * (floor-Y queries from shell-casing/grenade/ragdoll code, etc.).
 *
 * Named distinctly from `map/occlusion.getCollisionBVH()` to avoid
 * import collisions in modules that already pull from occlusion.
 *
 * Returns null until `setCollisionBVH` has been called (i.e. before
 * scene load, or on JSON-map paths that never built a BVH).
 */
export function getActiveCollisionBVH(): { bvh: MeshBVH; mesh: THREE.Mesh } | null {
  if (!worldBVH || !worldBVHMesh) return null;
  return { bvh: worldBVH, mesh: worldBVHMesh };
}

// ── Legacy AABB API — preserved as no-op stubs ──
//
// F-GP-007 (audits/2026-05-07): the legacy `colliders[]` AABB list was
// written by hundreds of `addBoxCollider` calls in `map/` but never read —
// the active path is the `worldBVH` shapecast above. CLAUDE.md says
// "NEVER use simple AABB on the client". The functions are kept as
// type-safe no-ops so the dozens of legacy callers in `map/brushes.ts`,
// `map/props.ts`, and `map/map-loader.ts` continue to compile without
// dragging this fix outside the client-gameplay scope.

export interface AABB {
  minX: number; maxX: number;
  minY: number; maxY: number;
  minZ: number; maxZ: number;
}

export function addBoxCollider(
  _cx: number, _cy: number, _cz: number,
  _w: number, _h: number, _d: number,
): void {
  // No-op — collision is resolved against `worldBVH` only. See header comment.
}

export function clearColliders(): void {
  // No-op — collision is resolved against `worldBVH` only. See header comment.
}

// ── Constants ──

/** Max walkable slope: surfaces with push direction Y > this are "floor".
 *  cos(46°) ≈ 0.694 — close to Source engine's 0.7 (45°).
 *  Tightened from 50° to prevent climbing steep angled walls. */
const MAX_SLOPE_DOT = Math.cos(46 * Math.PI / 180);

/** Skin width added to each push to prevent floating-point re-entry. */
const SKIN = 0.001;

/** Max collision resolution iterations. Each pass resolves penetrations from
 *  one BVH traversal, then re-checks from the new position. Corners and
 *  multi-surface contacts typically converge in 2-3 passes. */
const MAX_ITERATIONS = 5;

/** Minimum penetration depth worth resolving (avoids infinite micro-loops). */
const MIN_DEPTH = 1e-4;

// ── Temp objects (avoid per-frame allocation) ──

const _capsuleLine = new THREE.Line3();
const _box = new THREE.Box3();
const _triPoint = new THREE.Vector3();
const _capsulePoint = new THREE.Vector3();
const _pushDir = new THREE.Vector3();
const _deltaVec = new THREE.Vector3();

// ── Result type ──

export interface CollisionResult {
  x: number; y: number; z: number;
  vx: number; vy: number; vz: number;
  grounded: boolean;
}

/**
 * Resolve player position against all scene geometry.
 *
 * Uses capsule-vs-BVH shapecast when available, with fallback to
 * simple ground plane + arena bounds.
 *
 * @param x,y,z — proposed position (player feet, after velocity integration)
 * @param vx,vy,vz — current velocity (zeroed on blocked axes)
 * @param radius — player collision radius
 * @param height — player standing/crouching height
 * @param wasGrounded — whether player was grounded last tick
 */
export function resolveCollisions(
  x: number, y: number, z: number,
  vx: number, vy: number, vz: number,
  radius: number,
  height: number,
  wasGrounded: boolean,
): CollisionResult {
  let grounded = false;

  if (worldBVH) {
    // ── Capsule-vs-BVH shapecast (iterative multi-pass) ──

    // Build capsule line segment (bottom sphere center → top sphere center)
    _capsuleLine.start.set(x, y + radius, z);
    _capsuleLine.end.set(x, y + height - radius, z);

    // Store original position for total-displacement calculation
    const origStartX = _capsuleLine.start.x;
    const origStartY = _capsuleLine.start.y;
    const origStartZ = _capsuleLine.start.z;

    for (let iter = 0; iter < MAX_ITERATIONS; iter++) {
      // ── Build fresh AABB from current capsule position ──
      // This AABB stays CONSTANT during the shapecast pass so that the
      // BVH traversal (which calls intersectsBounds per-node) sees a
      // stable region and doesn't prune subtrees that contain wall
      // triangles the capsule still overlaps.
      _box.makeEmpty();
      _box.expandByPoint(_capsuleLine.start);
      _box.expandByPoint(_capsuleLine.end);
      _box.min.x -= radius;
      _box.min.y -= radius;
      _box.min.z -= radius;
      _box.max.x += radius;
      _box.max.y += radius;
      _box.max.z += radius;

      let hadContact = false;

      // ── Single shapecast pass ──
      // Push the capsule for EVERY penetrating triangle (not just the
      // deepest). This correctly resolves floor + wall simultaneously.
      // The AABB (_box) is NOT modified during the pass.
      worldBVH.shapecast({
        intersectsBounds: (bounds: THREE.Box3) => bounds.intersectsBox(_box),

        intersectsTriangle: (tri: any) => {
          tri.closestPointToSegment(_capsuleLine, _triPoint, _capsulePoint);

          const dist = _triPoint.distanceTo(_capsulePoint);
          const depth = radius - dist;

          if (depth > MIN_DEPTH) {
            hadContact = true;

            // Push direction: from triangle surface toward capsule center
            _pushDir.copy(_capsulePoint).sub(_triPoint);

            if (_pushDir.lengthSq() < 1e-10) {
              // Degenerate: capsule center is ON the triangle → use normal
              tri.getNormal(_pushDir);
            } else {
              _pushDir.normalize();
            }

            // Classify surface by the Y component of the push direction
            if (_pushDir.y > MAX_SLOPE_DOT) {
              // ── Walkable floor / gentle slope ──
              // Push along full 3D direction (lifts player onto surface)
              _capsuleLine.start.addScaledVector(_pushDir, depth + SKIN);
              _capsuleLine.end.addScaledVector(_pushDir, depth + SKIN);
            } else if (_pushDir.y < -0.9) {
              // ── Ceiling ──
              _capsuleLine.start.addScaledVector(_pushDir, depth + SKIN);
              _capsuleLine.end.addScaledVector(_pushDir, depth + SKIN);
            } else {
              // ── Wall or steep slope ──
              // Zero the Y component and re-normalize to prevent climbing.
              // Push horizontally only, using the HORIZONTAL component of
              // the penetration depth.
              const hx = _pushDir.x;
              const hz = _pushDir.z;
              const hLen = Math.sqrt(hx * hx + hz * hz);
              if (hLen > 1e-6) {
                const nx = hx / hLen;
                const nz = hz / hLen;
                // Scale depth by the horizontal fraction of the original
                // push direction so we don't over-push when the original
                // direction was mostly vertical.
                const hDepth = (depth + SKIN) * hLen;
                _capsuleLine.start.x += nx * hDepth;
                _capsuleLine.start.z += nz * hDepth;
                _capsuleLine.end.x += nx * hDepth;
                _capsuleLine.end.z += nz * hDepth;
              }
            }
          }

          // DO NOT update _box here — keep it constant for the entire pass
          return false; // Continue checking all triangles
        },
      });

      // If no triangle touched the capsule, we're fully resolved
      if (!hadContact) break;
    }

    // ── Compute total displacement ──
    _deltaVec.set(
      _capsuleLine.start.x - origStartX,
      _capsuleLine.start.y - origStartY,
      _capsuleLine.start.z - origStartZ,
    );

    // Extract corrected position (capsule bottom sphere center → feet)
    x = _capsuleLine.start.x;
    y = _capsuleLine.start.y - radius;
    z = _capsuleLine.start.z;

    // ── Ground detection ──
    if (_deltaVec.y > SKIN) {
      grounded = true;
      if (vy < 0) vy = 0;
    }

    // ── Ceiling detection ──
    if (_deltaVec.y < -SKIN) {
      if (vy > 0) vy = 0;
    }

    // ── Wall velocity cancellation ──
    // Project velocity onto the wall plane instead of zeroing axes
    // independently (which sticks the player on diagonal walls). We use
    // the horizontal component of the collision displacement as the wall
    // normal and subtract the velocity component along that normal.
    const wnx = _deltaVec.x;
    const wnz = _deltaVec.z;
    const wnLenSq = wnx * wnx + wnz * wnz;
    if (wnLenSq > 1e-12) {
      const wnLen = Math.sqrt(wnLenSq);
      const nx = wnx / wnLen;
      const nz = wnz / wnLen;
      const vDotN = vx * nx + vz * nz;
      if (vDotN < 0) {
        // Only cancel the component pushing INTO the wall — lets the
        // player slide along the wall without sticking.
        vx -= vDotN * nx;
        vz -= vDotN * nz;
      }
    }
  }

  // ── Arena bounds (always applied) ──
  const limit = ARENA_HALF - radius;
  if (x < -limit) { x = -limit; vx = 0; }
  if (x >  limit) { x =  limit; vx = 0; }
  if (z < -limit) { z = -limit; vz = 0; }
  if (z >  limit) { z =  limit; vz = 0; }

  // ── Ground floor ──
  if (y < 0) { y = 0; vy = 0; grounded = true; }

  return { x, y, z, vx, vy, vz, grounded };
}
